import { EventStatus } from "~/components/Events/event-status";

export const isReadOnly = (status) =>
    status === EventStatus.RECEIVED ||
    status === EventStatus.SCHEDULED ||
    status === EventStatus.MISSING;

export const hasValue = (value) =>
    !(value === null || value === undefined || (Array.isArray(value) && value.length === 0) || value === "");

/**
 * The empty value for an item, by type. Checkboxes hold an array so their
 * empty value is `[]`; everything else clears to `null`. Used when clearing a
 * hidden item's stored value (form-visibility) and when a computed item's
 * expression stops yielding a value (useFunctionRecompute).
 */
export const getDefaultItemValue = (itemType) =>
    itemType === "Checkboxes" ? [] : null;

export const countFormItems = (itemGroups) =>
    itemGroups.reduce(
        (itemCount, itemGroup) => itemCount + itemGroup.items.length,
        0
    );

export const isDateTimeItem = (itemType) =>
    itemType === "Date" || itemType === "DateAndTime" || itemType === "Time";

export const isTextItem = (itemType) =>
    itemType === "ParagraphText" ||
    itemType === "SingleLineText" ||
    itemType === "Number";

export const isStaticItemType = (itemType) =>
    itemType === "StaticText" || itemType === "SectionBreak";

// Item types whose control is a composite div-based widget, so the item id
// never lands on a native labelable element (input/select/textarea/button).
const compositeItemTypes = ["RadioButtons", "Checkboxes", "Nrs", "VasScale"];

// Text items swap their native control for a read-only div when readOnly.
const readOnlyCompositeItemTypes = ["SingleLineText", "ParagraphText"];

/**
 * Whether the item renders its id on a native labelable element, meaning the
 * item label can use <label for>. Composite widgets must be named through
 * aria-labelledby instead — a <label for> pointing at a div is invalid HTML
 * and is flagged by browser accessibility audits.
 */
export const hasLabelableControl = ({ itemType, htmlType, readOnly }) => {
    if (compositeItemTypes.includes(itemType)) return false;
    // The drawing-pad variant of FileUpload renders a div-based canvas
    // (role=group), not the native file input a plain FileUpload uses.
    if (itemType === "FileUpload" && htmlType === "drawingpad") return false;
    if (readOnly && readOnlyCompositeItemTypes.includes(itemType)) return false;
    return true;
};

/**
 * Maps every item OID in the form to its itemType. Used to type-coerce
 * formData values (stored as strings) before evaluating range-check
 * expressions, so relational operators compare numerically / chronologically
 * rather than lexicographically.
 *
 * Built fresh on each call rather than cached: callers invoke it once per
 * evaluation pass (already O(items)), so caching by itemGroups identity saved
 * little while making correctness depend on items never being mutated in place.
 * A fresh build always reflects the current form, including items added to an
 * existing itemGroups array. Every item is mapped regardless of visibility, so
 * expressions referencing a hidden item still coerce its value.
 */
export const buildItemTypeMap = (form) => {
    const itemGroups = form?.itemGroups;
    if (!itemGroups) return {};

    const map = {};
    for (const itemGroup of itemGroups) {
        for (const item of itemGroup.items ?? []) {
            if (item.oid) map[item.oid] = item.itemType;
        }
    }
    return map;
};

const getMaxDate = (maxValue, eventDate, studyEventId) => {
    if (!maxValue) {
        throw Error("Date item maxValue not set");
    }

    const isEventDate =
        maxValue === "EventDate" && studyEventId > 0 && eventDate;

    return isEventDate ? new Date(eventDate) : new Date();
};

const hasMaxDate = (maxValue) =>
    maxValue === "Today" || maxValue === "EventDate";

export const setMaxDate = (
    itemGroups,
    { EventDate: eventDate, StudyEventId: studyEventId }
) => {
    for (const itemGroup of itemGroups ?? []) {
        for (const item of itemGroup.items ?? []) {
            const maxValue = item.validation?.maxValue || item.validation?.max;

            if (
                isDateTimeItem(item.itemType) &&
                hasMaxDate(item.validation?.maxValue)
            ) {
                item.maxDate = getMaxDate(maxValue, eventDate, studyEventId);
            }
        }
    }
};

export const getDeviceInformation = () => {
    //Note: screen.orientation is not well supported by browsers
    //var screenOrientation = screen.orientation != null ? screen.orientation.type : null;

    const mql = window.matchMedia("(orientation: portrait)");

    // If there are matches, we're in portrait
    const screenOrientation = mql.matches ? "portrait" : "landscape";

    return {
        screenOrientation: screenOrientation,
        viewportHeight: window.innerHeight,
        viewportWidth: window.innerWidth,
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
    };
};

/**
 * Whether an item's validation marks it required. Accepts both wire shapes
 * (`required: true` and `required: { value: true }`) so the page validation
 * rules and the submit page's unanswered check can never disagree.
 */
export const isItemRequired = (validation) =>
    validation?.required === true || Boolean(validation?.required?.value);

const isRequiredItemWithoutValue = (item, formData) => {
    const itemHasNoValue = !hasValue(formData[item.oid]);
    const isNotStatic = !isStaticItemType(item.itemType);
    const isRequired = isItemRequired(item.validation);
    const isVisible = item.isVisible;

    return itemHasNoValue && isNotStatic && isRequired && isVisible;
};

export const getItemsWithoutValue = (itemGroups, formData) =>
    itemGroups
        .filter((ig) => ig.isVisible)
        .reduce(
            (acc, ig, i) => {
                const requiredItemsWithoutValue = ig.items
                    .filter((it) => isRequiredItemWithoutValue(it, formData))
                    .map((item) => ({
                        page: i + 1,
                        item,
                    }));
                
                for (const item of requiredItemsWithoutValue) {
                    acc.push(item);
                }

                return acc;
            },
            []
        );
